import {
  ChevronRight,
  Copy,
  CopyMinus,
  File,
  FilePlus2,
  Folder,
  FolderInput,
  FolderPlus,
  MoreHorizontal,
  ListFilter,
  Pencil,
  Trash2,
  X,
  Lock,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { toast } from "sonner";
import { can, isSecretPath, type TreeNode } from "@ide/shared";
import type { Peer } from "@/collab/session";
import { Button } from "@/components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Tip,
} from "@/components/ui/overlay";
import { PanelHeader } from "@/components/ui/panel-header";
import { Select } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { fileIconKey, FileTypeIcon, GenericFileIcon } from "@/components/file-icons";
import { useColoredIcons } from "@/plugins/use-plugins";
import { usePeers, useRole, useTree, useWs } from "./context";
import { useGitChanges } from "./git-store";
import { useWorkspace } from "./store";

/**
 * Ícone de um arquivo, na árvore, nas abas, na busca e no Git: dá para saber o tipo de relance.
 * Sem a extensão "Ícones de arquivo", o ícone neutro do Kompil (documento com a sigla: JS, PY,
 * {}…); com ela, o colorido de cada linguagem. Arquivo sem tipo conhecido: o documento simples.
 */
export function fileIcon(name: string, className = "h-4 w-4", colored = true) {
  // Como no VS Code (tema Seti): todo arquivo tem ícone, inclusive os sem tipo conhecido.
  if (colored) return <FileTypeIcon name={name} className={cn("shrink-0", className)} colored />;
  return fileIconKey(name) === "file" ? (
    <GenericFileIcon className={cn("shrink-0 text-fg-subtle", className)} />
  ) : (
    <FileTypeIcon name={name} className={cn("shrink-0", className)} colored={false} />
  );
}

type Draft =
  { mode: "create"; parentId: string | null; type: "file" | "folder" } | { mode: "rename"; id: string };

const ROW = 22;
const INDENT = 10;

/** `expanded` acrescido da pasta e de todos os ancestrais; o mesmo Set se nada mudar. */
function withAncestors(
  expanded: Set<string>,
  nodes: ReadonlyMap<string, TreeNode>,
  folderId: string | null,
): Set<string> {
  let next = expanded;
  // Limite de profundidade: movimentos concorrentes podem formar ciclos na árvore do CRDT.
  for (let id = folderId, depth = 0; id && depth < 256; id = nodes.get(id)?.parentId ?? null, depth++) {
    if (next.has(id)) continue;
    if (next === expanded) next = new Set(expanded);
    next.add(id);
  }
  return next;
}

function InlineInput({
  initial,
  onSubmit,
  onCancel,
  depth,
  icon,
}: {
  initial: string;
  onSubmit: (v: string) => void;
  onCancel: () => void;
  depth: number;
  icon: ReactNode;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const mountedAt = useRef(0);
  const done = useRef(false);
  // O menu que pediu "Novo arquivo" devolve o foco ao botão quando fecha — depois de o campo
  // aparecer. O foco vai para o campo de novo nos quadros seguintes, e um "blur" logo no começo
  // (o foco roubado pelo menu) não fecha o campo: ele espera o nome.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    mountedAt.current = Date.now();
    const focus = () => {
      if (done.current || !ref.current) return;
      ref.current.focus();
      const dot = initial.lastIndexOf(".");
      ref.current.setSelectionRange(0, dot > 0 ? dot : initial.length);
    };
    focus();
    const raf = requestAnimationFrame(focus);
    const t1 = setTimeout(focus, 50);
    const t2 = setTimeout(focus, 180);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [initial]);
  const submit = (value: string) => {
    if (done.current) return;
    done.current = true;
    onSubmit(value);
  };
  const cancel = () => {
    if (done.current) return;
    done.current = true;
    onCancel();
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      if (e.currentTarget.value.trim()) submit(e.currentTarget.value);
      else e.preventDefault();
    }
    if (e.key === "Escape") cancel();
  };
  return (
    <div className="flex items-center gap-1.5 pr-2" style={{ height: ROW, paddingLeft: 6 + depth * INDENT }}>
      <span className="w-3.5" />
      <span className="text-fg-subtle">{icon}</span>
      <input
        ref={ref}
        defaultValue={initial}
        onKeyDown={onKey}
        onBlur={(e) => {
          const el = e.currentTarget;
          if (el.value.trim()) return submit(el.value);
          // Vazio: se o foco saiu logo depois de abrir (o menu fechando), volta para o campo.
          if (Date.now() - mountedAt.current < 400) {
            requestAnimationFrame(() => !done.current && ref.current?.focus());
            return;
          }
          cancel();
        }}
        placeholder="nome"
        className="h-[20px] min-w-0 flex-1 rounded-[3px] border border-[var(--focus,var(--fg))] bg-bg px-1 font-mono text-[12.5px] outline-none"
        aria-label="Nome"
      />
    </div>
  );
}

/**
 * Arquivos que casam com o filtro (no nome; com "/", no caminho) e as pastas até eles.
 * null: sem filtro.
 */
export function filterTree(
  query: string,
  nodes: ReadonlyMap<string, TreeNode>,
  paths: ReadonlyMap<string, string>,
): Set<string> | null {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  const byPath = q.includes("/");
  const keep = new Set<string>();
  for (const n of nodes.values()) {
    if (n.type !== "file") continue;
    const hay = byPath ? (paths.get(n.id) ?? n.name) : n.name;
    if (!hay.toLowerCase().includes(q)) continue;
    keep.add(n.id);
    for (let id = n.parentId, depth = 0; id && depth < 256; id = nodes.get(id)?.parentId ?? null, depth++) {
      keep.add(id);
    }
  }
  return keep;
}

function Match({ text, query }: { text: string; query: string }) {
  const q = query.trim().toLowerCase();
  const i = q && !q.includes("/") ? text.toLowerCase().indexOf(q) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <span className="font-semibold text-fg underline decoration-fg-subtle underline-offset-2">
        {text.slice(i, i + q.length)}
      </span>
      {text.slice(i + q.length)}
    </>
  );
}

function copy(text: string) {
  void navigator.clipboard.writeText(text).then(
    () => toast("Caminho copiado."),
    () => toast.error("Não foi possível copiar."),
  );
}

export function FileTree() {
  const { session, detail } = useWs();
  const coloredIcons = useColoredIcons();
  const tree = useTree();
  const peers = usePeers();
  const role = useRole();
  const canWrite = can(role, "write");
  const { active, openFile } = useWorkspace();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [section, setSection] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [moving, setMoving] = useState<TreeNode | null>(null);
  const [deleting, setDeleting] = useState<TreeNode | null>(null);
  const [filtering, setFiltering] = useState(false);
  const [filter, setFilter] = useState("");
  const visible = useMemo(() => filterTree(filter, tree.nodes, tree.paths), [filter, tree]);
  // Marcas do Git (M/A) nos arquivos e um ponto nas pastas com alterações dentro.
  const gitChanges = useGitChanges((s) => s.changes);
  const gitConflicts = useGitChanges((s) => s.conflicts);
  const gitMarks = useMemo(() => {
    const files = new Map<string, string>();
    const folders = new Set<string>();
    for (const c of gitChanges ?? []) {
      if (c.kind === "D") continue;
      files.set(c.path, gitConflicts.includes(c.path) ? "!" : c.kind);
      const parts = c.path.split("/");
      for (let i = 1; i < parts.length; i++) folders.add(parts.slice(0, i).join("/"));
    }
    return { files, folders };
  }, [gitChanges, gitConflicts]);

  // "Novo arquivo/Nova pasta" pelo menu do app desktop (pode chegar antes desta árvore montar).
  useEffect(() => {
    const take = (req: { type: "file" | "folder" } | null) => {
      if (!req) return;
      useWorkspace.setState({ createRequest: null });
      setSection(true);
      setDraft({ mode: "create", parentId: null, type: req.type });
    };
    queueMicrotask(() => take(useWorkspace.getState().createRequest));
    return useWorkspace.subscribe((s, prev) => {
      if (s.createRequest && s.createRequest !== prev.createRequest) take(s.createRequest);
    });
  }, []);

  const peersByFile = useMemo(() => {
    const map = new Map<string, Peer[]>();
    for (const p of peers) {
      if (p.isSelf || !p.currentFile) continue;
      map.set(p.currentFile, [...(map.get(p.currentFile) ?? []), p]);
    }
    return map;
  }, [peers]);

  // Expande as pastas do arquivo ativo quando ele muda, é movido ou a árvore chega.
  // Ajuste de estado durante o render (sem efeito): a pessoa ainda pode recolher depois.
  const activeNode = active ? tree.nodes.get(active) : undefined;
  const revealKey = activeNode ? `${activeNode.id}:${activeNode.parentId ?? ""}` : null;
  const [revealed, setRevealed] = useState<string | null>(null);
  if (revealKey !== revealed) {
    setRevealed(revealKey);
    if (activeNode) {
      setSelected(activeNode.id);
      const next = withAncestors(expanded, tree.nodes, activeNode.parentId);
      if (next !== expanded) setExpanded(next);
    }
  }

  const run = (fn: () => void) => {
    try {
      fn();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível concluir.");
    }
  };

  const submitDraft = (value: string) => {
    const d = draft;
    setDraft(null);
    if (!d) return;
    run(() => {
      if (d.mode === "create") {
        const id = session.createNode(d.parentId, value, d.type);
        setSelected(id);
        if (d.type === "file") openFile(id);
        else setExpanded((s) => new Set(s).add(id));
      } else {
        session.renameNode(d.id, value);
      }
    });
  };

  const toggleFolder = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const startCreate = (parentId: string | null, type: "file" | "folder") => {
    setSection(true);
    if (parentId) setExpanded((s) => new Set(s).add(parentId));
    setDraft({ mode: "create", parentId, type });
  };

  /** Pasta onde "Novo arquivo" cria: a própria pasta ou a pasta do arquivo selecionado. */
  const folderOf = (id: string | null): string | null => {
    const node = id ? tree.nodes.get(id) : undefined;
    if (!node) return null;
    return node.type === "folder" ? node.id : node.parentId;
  };

  const pathOf = (node: TreeNode) => tree.paths.get(node.id) ?? node.name;

  // F2 renomeia e Delete exclui o item selecionado (com a árvore em foco).
  const onTreeKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const node = selected ? tree.nodes.get(selected) : undefined;
    if (!node || !canWrite || draft) return;
    if (e.key === "F2") {
      e.preventDefault();
      setDraft({ mode: "rename", id: node.id });
    } else if (e.key === "Delete") {
      e.preventDefault();
      setDeleting(node);
    }
  };

  /** Itens do menu (botão direito e "…"). `C` escolhe entre o menu de contexto e o suspenso. */
  const actions = (node: TreeNode, kind: "context" | "menu") => {
    const Item = kind === "context" ? ContextMenuItem : MenuItem;
    const Sep = kind === "context" ? ContextMenuSeparator : MenuSeparator;
    const isFolder = node.type === "folder";
    const parent = isFolder ? node.id : node.parentId;
    return (
      <>
        {!isFolder ? (
          <>
            <Item icon={<File className="h-3.5 w-3.5" />} onSelect={() => openFile(node.id)}>
              Abrir
            </Item>
            <Sep />
          </>
        ) : null}
        {canWrite ? (
          <>
            <Item icon={<FilePlus2 className="h-3.5 w-3.5" />} onSelect={() => startCreate(parent, "file")}>
              Novo arquivo…
            </Item>
            <Item
              icon={<FolderPlus className="h-3.5 w-3.5" />}
              onSelect={() => startCreate(parent, "folder")}
            >
              Nova pasta…
            </Item>
            <Sep />
          </>
        ) : null}
        <Item icon={<Copy className="h-3.5 w-3.5" />} onSelect={() => copy(pathOf(node))}>
          Copiar caminho
        </Item>
        {canWrite ? (
          <>
            <Sep />
            <Item
              icon={<Pencil className="h-3.5 w-3.5" />}
              shortcut="F2"
              onSelect={() => setDraft({ mode: "rename", id: node.id })}
            >
              Renomear…
            </Item>
            <Item icon={<FolderInput className="h-3.5 w-3.5" />} onSelect={() => setMoving(node)}>
              Mover para…
            </Item>
            <Item icon={<Trash2 className="h-3.5 w-3.5" />} shortcut="Del" onSelect={() => setDeleting(node)}>
              Excluir
            </Item>
          </>
        ) : null}
      </>
    );
  };

  const render = (parentId: string | null, depth: number): ReactNode => {
    const children = tree.children.get(parentId) ?? [];
    return (
      <>
        {draft?.mode === "create" && draft.parentId === parentId ? (
          <InlineInput
            initial=""
            depth={depth}
            icon={
              draft.type === "folder" ? <Folder className="h-3.5 w-3.5" /> : <File className="h-3.5 w-3.5" />
            }
            onSubmit={submitDraft}
            onCancel={() => setDraft(null)}
          />
        ) : null}
        {children.map((node) => {
          if (visible && !visible.has(node.id)) return null;
          const isFolder = node.type === "folder";
          // Com filtro, as pastas do caminho até o que casou ficam abertas.
          const open = visible ? true : expanded.has(node.id);
          if (draft?.mode === "rename" && draft.id === node.id) {
            return (
              <InlineInput
                key={node.id}
                initial={node.name}
                depth={depth}
                icon={isFolder ? null : fileIcon(node.name, undefined, coloredIcons)}
                onSubmit={submitDraft}
                onCancel={() => setDraft(null)}
              />
            );
          }
          const here = peersByFile.get(node.id) ?? [];
          const isActive = active === node.id;
          const isSelected = selected === node.id;
          return (
            <div
              key={node.id}
              role="treeitem"
              aria-expanded={isFolder ? open : undefined}
              aria-selected={isActive}
              className="relative"
            >
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <div
                    className={cn(
                      "group relative flex cursor-pointer select-none items-center gap-1.5 pr-1 text-[13px] outline-none",
                      isActive
                        ? "bg-bg-emphasis text-fg"
                        : isSelected
                          ? "bg-bg-muted text-fg"
                          : "text-fg-muted hover:bg-bg-muted/70 hover:text-fg",
                      "data-[state=open]:outline data-[state=open]:outline-1 data-[state=open]:-outline-offset-1 data-[state=open]:outline-fg-subtle",
                    )}
                    style={{ height: ROW, paddingLeft: 6 + depth * INDENT }}
                    onClick={() => {
                      setSelected(node.id);
                      if (isFolder) toggleFolder(node.id);
                      else openFile(node.id);
                    }}
                  >
                    <ChevronRight
                      className={cn(
                        "h-3.5 w-3.5 shrink-0 text-fg-subtle transition-transform",
                        isFolder ? "" : "invisible",
                        open && "rotate-90",
                      )}
                    />
                    {/* Pastas sem ícone, como no tema Seti do VS Code: só a seta. */}
                    {isFolder ? null : fileIcon(node.name, undefined, coloredIcons)}
                    <span className="min-w-0 flex-1 truncate">
                      {visible ? <Match text={node.name} query={filter} /> : node.name}
                    </span>
                    {!isFolder && detail.canSeeEnv === false && isSecretPath(node.name) ? (
                      <Lock
                        className="mr-1 h-3 w-3 shrink-0 text-fg-subtle"
                        aria-label="Restrito: sem a permissão Ver .env"
                      />
                    ) : null}
                    {(() => {
                      const path = tree.paths.get(node.id);
                      if (!path) return null;
                      if (isFolder) {
                        return gitMarks.folders.has(path) ? (
                          <span
                            className="mr-1 h-1.5 w-1.5 shrink-0 rounded-full bg-fg-subtle"
                            title="Alterações não commitadas aqui dentro"
                          />
                        ) : null;
                      }
                      const mark = gitMarks.files.get(path);
                      return mark ? (
                        <span
                          className={cn(
                            "w-3 shrink-0 text-center font-mono text-[10.5px] font-semibold",
                            mark === "!" ? "text-[var(--warning)]" : "text-fg-subtle",
                          )}
                          title={
                            mark === "!"
                              ? "Conflito no último pull"
                              : mark === "A"
                                ? "Novo (Git)"
                                : "Modificado (Git)"
                          }
                        >
                          {mark}
                        </span>
                      ) : null;
                    })()}
                    {here.length > 0 ? (
                      <span
                        className="flex shrink-0 items-center gap-0.5"
                        aria-label={`Aqui: ${here.map((p) => p.user.name).join(", ")}`}
                      >
                        {here.slice(0, 3).map((p) => (
                          <Tip key={p.clientId} label={p.user.name}>
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: p.user.color }}
                            />
                          </Tip>
                        ))}
                      </span>
                    ) : null}
                    <Menu>
                      <MenuTrigger asChild>
                        <button
                          className="invisible rounded-sm p-0.5 text-fg-subtle hover:bg-bg-emphasis hover:text-fg group-hover:visible data-[state=open]:visible"
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Ações de ${node.name}`}
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </button>
                      </MenuTrigger>
                      <MenuContent align="start" onClick={(e) => e.stopPropagation()}>
                        {actions(node, "menu")}
                      </MenuContent>
                    </Menu>
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent onClick={(e) => e.stopPropagation()}>
                  {actions(node, "context")}
                </ContextMenuContent>
              </ContextMenu>
              {isFolder && open ? (
                <div role="group" className="relative">
                  {/* Guia de indentação, como nos editores de código. */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute bottom-0 top-0 w-px bg-border"
                    style={{ left: 6 + depth * INDENT + 7 }}
                  />
                  {render(node.id, depth + 1)}
                </div>
              ) : null}
            </div>
          );
        })}
      </>
    );
  };

  const folders = [...tree.nodes.values()].filter((n) => n.type === "folder" && tree.paths.has(n.id));
  const headerButton = (label: string, icon: ReactNode, onClick: () => void) => (
    <Tip label={label}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        aria-label={label}
        className="flex h-[22px] w-[22px] items-center justify-center rounded-[4px] text-fg-muted hover:bg-bg-emphasis hover:text-fg"
      >
        {icon}
      </button>
    </Tip>
  );

  return (
    <div className="flex h-full flex-col">
      <PanelHeader
        title="Explorador"
        actions={headerButton(
          filtering ? "Fechar filtro" : "Filtrar arquivos",
          <ListFilter className="h-3.5 w-3.5" />,
          () => {
            if (filtering) {
              setFiltering(false);
              setFilter("");
            } else {
              setFiltering(true);
              setSection(true);
            }
          },
        )}
      />
      {filtering ? (
        <div className="relative shrink-0 px-2 pb-2">
          <input
            autoFocus
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                setFilter("");
                setFiltering(false);
              }
            }}
            placeholder="Filtrar pelo nome (ou caminho com /)"
            aria-label="Filtrar arquivos"
            spellCheck={false}
            className="h-[26px] w-full rounded-md border border-border bg-bg pl-2 pr-7 text-[12.5px] text-fg outline-none placeholder:text-fg-subtle focus:border-ring"
          />
          {filter ? (
            <button
              onClick={() => setFilter("")}
              className="absolute right-3.5 top-[5px] flex h-4 w-4 items-center justify-center rounded text-fg-subtle hover:text-fg"
              aria-label="Limpar filtro"
            >
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      ) : null}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setSection((v) => !v)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setSection((v) => !v)}
        className="group mx-1.5 flex h-7 shrink-0 cursor-pointer select-none items-center gap-1 rounded-md pl-1 pr-1 text-[12.5px] font-semibold text-fg outline-none hover:bg-bg-muted/60 focus-visible:ring-1 focus-visible:ring-fg-subtle"
        aria-expanded={section}
      >
        <ChevronRight className={cn("h-3.5 w-3.5 shrink-0 transition-transform", section && "rotate-90")} />
        <span className="min-w-0 flex-1 truncate">{detail.project.name}</span>
        <span className="hidden items-center gap-0.5 group-hover:flex group-focus-within:flex">
          {canWrite ? (
            <>
              {headerButton("Novo arquivo…", <FilePlus2 className="h-3.5 w-3.5" />, () =>
                startCreate(folderOf(selected), "file"),
              )}
              {headerButton("Nova pasta…", <FolderPlus className="h-3.5 w-3.5" />, () =>
                startCreate(folderOf(selected), "folder"),
              )}
            </>
          ) : null}
          {headerButton("Recolher pastas", <CopyMinus className="h-3.5 w-3.5" />, () =>
            setExpanded(new Set()),
          )}
        </span>
      </div>
      {section ? (
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div
              role="tree"
              tabIndex={0}
              aria-label="Arquivos do projeto"
              onKeyDown={onTreeKey}
              className="min-h-0 flex-1 overflow-y-auto pb-6 outline-none"
            >
              {tree.nodes.size === 0 && !draft ? (
                <div className="px-5 py-4 text-[12.5px] leading-relaxed text-fg-muted">
                  <p>Projeto vazio.</p>
                  {canWrite ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={() => startCreate(null, "file")}
                    >
                      <FilePlus2 className="h-3.5 w-3.5" /> Criar arquivo
                    </Button>
                  ) : null}
                </div>
              ) : visible && visible.size === 0 ? (
                <p className="px-5 py-3 text-[12.5px] text-fg-subtle">
                  Nenhum arquivo com “{filter.trim()}”.
                </p>
              ) : (
                render(null, 0)
              )}
            </div>
          </ContextMenuTrigger>
          {canWrite ? (
            <ContextMenuContent>
              <ContextMenuItem
                icon={<FilePlus2 className="h-3.5 w-3.5" />}
                onSelect={() => startCreate(null, "file")}
              >
                Novo arquivo…
              </ContextMenuItem>
              <ContextMenuItem
                icon={<FolderPlus className="h-3.5 w-3.5" />}
                onSelect={() => startCreate(null, "folder")}
              >
                Nova pasta…
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem
                icon={<CopyMinus className="h-3.5 w-3.5" />}
                onSelect={() => setExpanded(new Set())}
              >
                Recolher pastas
              </ContextMenuItem>
            </ContextMenuContent>
          ) : null}
        </ContextMenu>
      ) : (
        <div className="flex-1" />
      )}

      <Dialog open={Boolean(moving)} onOpenChange={(o) => !o && setMoving(null)}>
        {moving ? (
          <DialogContent title={`Mover "${moving.name}"`}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const target = new FormData(e.currentTarget).get("target") as string;
                run(() => session.moveNode(moving.id, target || null));
                setMoving(null);
              }}
            >
              <Select name="target" defaultValue={moving.parentId ?? ""} aria-label="Pasta de destino">
                <option value="">/ (raiz)</option>
                {folders
                  .filter((f) => f.id !== moving.id)
                  .map((f) => (
                    <option key={f.id} value={f.id}>
                      /{tree.paths.get(f.id)}
                    </option>
                  ))}
              </Select>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMoving(null)}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary">
                  Mover
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        ) : null}
      </Dialog>

      <Dialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        {deleting ? (
          <DialogContent
            title={`Excluir "${deleting.name}"?`}
            description="A exclusão vale para todas as pessoas do projeto. Dá para desfazer pela gravação de sessões, se estiver ligada."
          >
            <p className="font-mono text-[12.5px] text-fg-muted">/{tree.paths.get(deleting.id)}</p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleting(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  run(() => session.deleteNode(deleting.id));
                  setDeleting(null);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" /> Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}
